/**
 * Shared helpers for Illustration OCR (Advisor-level module).
 * Consumers: Premium Financing, debug routes, and future advisor tools.
 */

import { tryTranslate } from '../../../utils/locale';

export const MAX_ILLUSTRATION_BYTES = 9 * 1024 * 1024;
/** Max saved illustrations per user — hide upload when at/over this count. */
export const MAX_ILLUSTRATIONS = 8;
/** Same code as server file-analyze `FILE-TOO-LARGE`. */
export const ILLUSTRATION_FILE_TOO_LARGE = 'FILE-TOO-LARGE';
export const ILLUSTRATION_DATA_NOT_FOUND = 'illustration_data_not_found';
export const ILLUSTRATION_CONTROL_FIELD_MISSING = 'CONTROL-FIELD-MISSING';

export const isIllustrationOcrFinalError = code =>
  code === ILLUSTRATION_CONTROL_FIELD_MISSING ||
  code === ILLUSTRATION_FILE_TOO_LARGE ||
  code === ILLUSTRATION_DATA_NOT_FOUND;
export const ILLUSTRATION_POLL_INTERVAL_MS = 2000;
// Keep the ~6 min total wait window (2s × 180) after dropping the interval to 2s.
export const ILLUSTRATION_MAX_POLL_ATTEMPTS = 180;
export const ILLUSTRATION_ACCEPT = 'application/pdf';
export const ILLUSTRATION_SCENARIO = 'base';

export const ILLUSTRATION_FEATURE_MEDICAL_RESERVE = 'medical-reserve';
export const ILLUSTRATION_FEATURE_PREMIUM_FINANCING = 'premium-financing';

const ILLUSTRATION_FEATURE_ALIASES = {
  'medical-reserve': ILLUSTRATION_FEATURE_MEDICAL_RESERVE,
  medicalreserve: ILLUSTRATION_FEATURE_MEDICAL_RESERVE,
  'premium-financing': ILLUSTRATION_FEATURE_PREMIUM_FINANCING,
  premiumfinancing: ILLUSTRATION_FEATURE_PREMIUM_FINANCING,
  pfin: ILLUSTRATION_FEATURE_PREMIUM_FINANCING,
  'p-fin': ILLUSTRATION_FEATURE_PREMIUM_FINANCING
};

export const normalizeIllustrationFeature = raw => {
  if (raw == null || raw === '') return null;
  const value = String(raw).trim();
  if (!value) return null;
  const alias = ILLUSTRATION_FEATURE_ALIASES[value.toLowerCase().replace(/_/g, '-')];
  return alias || value;
};

export const normalizeIllustrationFeatures = features => {
  const seen = new Set();
  const next = [];
  (Array.isArray(features) ? features : []).forEach(item => {
    const key = normalizeIllustrationFeature(item);
    if (!key || seen.has(key)) return;
    seen.add(key);
    next.push(key);
  });
  return next;
};

export const getIllustrationFeatureLabel = (feature, intl) => {
  const key = normalizeIllustrationFeature(feature);
  if (!key) return '';
  if (key === ILLUSTRATION_FEATURE_MEDICAL_RESERVE) {
    return intl ? tryTranslate(intl, 'illustration-ocr-feature-medical-reserve') : key;
  }
  if (key === ILLUSTRATION_FEATURE_PREMIUM_FINANCING) {
    return intl ? tryTranslate(intl, 'illustration-ocr-feature-premium-financing') : key;
  }
  return intl ? tryTranslate(intl, `illustration-ocr-feature-${key}`) : key;
};

export const getIllustrationLinkedMap = linked => {
  if (!linked || typeof linked !== 'object' || Array.isArray(linked)) return {};
  const entries = linked instanceof Map ? [...linked.entries()] : Object.entries(linked);
  const next = {};
  entries.forEach(([rawKey, rawIds]) => {
    const key = normalizeIllustrationFeature(rawKey);
    if (!key) return;
    const list = Array.isArray(rawIds) ? rawIds : rawIds == null || rawIds === '' ? [] : [rawIds];
    const ids = [];
    const seen = new Set();
    list.forEach(item => {
      const id =
        item && typeof item === 'object' ? String(item._id || item.id || '').trim() : String(item || '').trim();
      if (!id || seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
    if (ids.length) next[key] = ids;
  });
  return next;
};

/** Stamp origin on Save. Does not invent a default when `feature` is omitted. */
export const withIllustrationOriginFeature = (record, feature) => {
  const key = normalizeIllustrationFeature(feature);
  if (!record || !key) return record;
  if (normalizeIllustrationFeature(record.meta?.feature_origin) === key) return record;
  const restMeta = { ...(record.meta || {}) };
  delete restMeta.feature;
  delete restMeta.features;
  return {
    ...record,
    meta: {
      ...restMeta,
      feature_origin: key
    }
  };
};

export const getIllustrationFeatureKeys = record =>
  normalizeIllustrationFeatures(Object.keys(getIllustrationLinkedMap(record?.meta?.linked)));

export const toIllustrationLinkedId = raw => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return toIllustrationLinkedId(raw._id || raw.id);
  const value = String(raw).trim();
  return value && value !== 'null' && value !== 'undefined' ? value : null;
};

export const formatBytes = n => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Reject blobs/files over MAX_ILLUSTRATION_BYTES (9 MB), matching server assertion.
 * @throws {{ code: 'FILE-TOO-LARGE', message: string }}
 */
export const assertIllustrationFileSize = (fileOrBlob, message) => {
  const size = fileOrBlob?.size;
  if (size == null || size > MAX_ILLUSTRATION_BYTES) {
    const err = new Error(message || `File exceeds ${formatBytes(MAX_ILLUSTRATION_BYTES)} limit`);
    err.code = ILLUSTRATION_FILE_TOO_LARGE;
    throw err;
  }
};

export const formatNum = v => {
  if (v === null || v === undefined) return '—';
  if (typeof v !== 'number' || Number.isNaN(v)) return '—';
  return v.toLocaleString('en-US');
};

export const formatPct = v => {
  if (v === null || v === undefined) return '—';
  return `${v}%`;
};

export const formatPremium = (amount, currency) => {
  if (amount == null && !currency) return null;
  const formatted =
    typeof amount === 'number' && !Number.isNaN(amount)
      ? amount.toLocaleString('en-US')
      : amount != null
      ? String(amount)
      : null;
  if (formatted && currency) return `${formatted} ${currency}`;
  return formatted || currency || null;
};

/** Non-guaranteed = total − guaranteed (initial load only; edits store parts separately). */
export const nonGuaranteed = (total, guaranteed) => {
  if (total == null || guaranteed == null) return null;
  if (typeof total !== 'number' || typeof guaranteed !== 'number') return null;
  if (Number.isNaN(total) || Number.isNaN(guaranteed)) return null;
  return total - guaranteed;
};

const toEditNum = v => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Total displayed/saved as guaranteed + nonGuaranteed. */
export const sumMetricParts = (guaranteed, nonGuaranteedValue) => {
  const g = toEditNum(guaranteed);
  const ng = toEditNum(nonGuaranteedValue);
  if (g == null && ng == null) return null;
  return (g ?? 0) + (ng ?? 0);
};

const partsFromMetric = metric => {
  const g = toEditNum(metric?.guaranteed);
  const t = toEditNum(metric?.base?.total);
  let ng = null;
  if (t != null && g != null) ng = t - g;
  else if (t != null && g == null) ng = t;
  else if (t == null && g != null) ng = 0;
  return { guaranteed: g, nonGuaranteed: ng };
};

/** Editable parts for review UI: surrender + benefits rows + premium paid + summary meta. */
export const getMetricEditsFromRecord = record => {
  const projection = Array.isArray(record?.projection) ? record.projection : [];
  const meta = record?.meta || {};
  const insured = meta.insured || {};
  const premium = meta.premium || {};
  const insurerRef = meta.insurer;
  const insurerId =
    insurerRef && typeof insurerRef === 'object' ? insurerRef._id || insurerRef.id || null : insurerRef || null;

  return {
    surrender: projection.map(row => partsFromMetric(row.surrenderValue)),
    benefits: projection.map(row => partsFromMetric(row.deathBenefit)),
    premiumPaid: projection.map(row => toEditNum(row.totalPremiumPaid)),
    meta: {
      productName: meta.productName != null ? String(meta.productName) : '',
      insurer: insurerId != null ? String(insurerId) : '',
      gender: insured.gender || '',
      issueAge: toEditNum(insured.issueAge),
      smokingStatus: insured.smokingStatus || '',
      totalBasicPremium: toEditNum(premium.totalBasicPremium),
      initialSumInsured: toEditNum(meta.initialSumInsured),
      initialSurrenderValue: toEditNum(meta.initialSurrenderValue)
    }
  };
};

const applyMetricEdit = (metric, edit) => {
  const next = { ...(metric || {}) };
  const base = { ...(next.base || {}) };
  const g = toEditNum(edit?.guaranteed);
  const ng = toEditNum(edit?.nonGuaranteed);
  next.guaranteed = g;
  base.total = sumMetricParts(g, ng);
  next.base = base;
  return next;
};

/** Persist guaranteed + total (= guaranteed + nonGuaranteed) for both metrics + meta. */
export const buildRecordWithMetricEdits = (record, edits, { insurers } = {}) => {
  if (!record) return record;
  const projection = (Array.isArray(record.projection) ? record.projection : []).map((row, index) => {
    const next = {
      ...row,
      surrenderValue: applyMetricEdit(row.surrenderValue, edits?.surrender?.[index]),
      deathBenefit: applyMetricEdit(row.deathBenefit, edits?.benefits?.[index])
    };
    if (edits?.premiumPaid && index < edits.premiumPaid.length) {
      next.totalPremiumPaid = toEditNum(edits.premiumPaid[index]);
    }
    return next;
  });

  const meta = { ...(record.meta || {}) };
  const insured = { ...(meta.insured || {}) };
  const premium = { ...(meta.premium || {}) };

  if (edits?.meta) {
    if ('productName' in edits.meta) {
      const name = edits.meta.productName;
      meta.productName = name == null || name === '' ? null : String(name);
    }
    if ('insurer' in edits.meta) {
      const insurerId = edits.meta.insurer;
      if (insurerId == null || insurerId === '') {
        meta.insurer = null;
        meta.insurerCode = null;
      } else {
        meta.insurer = insurerId;
        const list = Array.isArray(insurers) ? insurers : [];
        const doc = list.find(i => String(i._id) === String(insurerId));
        if (doc?.insurerId) meta.insurerCode = doc.insurerId;
      }
    }
    if ('gender' in edits.meta) {
      const g = edits.meta.gender;
      insured.gender = g === 'male' || g === 'female' ? g : null;
    }
    if ('issueAge' in edits.meta) {
      insured.issueAge = toEditNum(edits.meta.issueAge);
    }
    if ('smokingStatus' in edits.meta) {
      const s = edits.meta.smokingStatus;
      insured.smokingStatus = s === 'smoker' || s === 'non_smoker' ? s : null;
    }
    if ('totalBasicPremium' in edits.meta) {
      premium.totalBasicPremium = toEditNum(edits.meta.totalBasicPremium);
    }
    if ('initialSumInsured' in edits.meta) {
      meta.initialSumInsured = toEditNum(edits.meta.initialSumInsured);
    }
    if ('initialSurrenderValue' in edits.meta) {
      meta.initialSurrenderValue = toEditNum(edits.meta.initialSurrenderValue);
    }
  }

  meta.insured = insured;
  meta.premium = premium;
  return { ...record, projection, meta };
};

/** @deprecated Prefer getMetricEditsFromRecord — kept for callers that only need surrender totals. */
export const getCashValuesFromRecord = record =>
  getMetricEditsFromRecord(record).surrender.map(part => sumMetricParts(part.guaranteed, part.nonGuaranteed));

/** @deprecated Prefer buildRecordWithMetricEdits */
export const buildRecordWithCashValues = (record, cashValues) => {
  if (!record) return record;
  const edits = getMetricEditsFromRecord(record);
  edits.surrender = edits.surrender.map((part, index) => {
    const total = toEditNum(cashValues?.[index]);
    if (total == null) return part;
    const g = toEditNum(part.guaranteed) ?? 0;
    return { guaranteed: g, nonGuaranteed: total - g };
  });
  return buildRecordWithMetricEdits(record, edits);
};

/**
 * Resolve insurer display name from store (+ locale messages), not raw OCR code.
 * Prefer: populated/store title[locale] → tryTranslate(insurerId/code) → raw code.
 */
export const getInsurerLabel = (meta, { insurers, locale, intl } = {}) => {
  if (!meta) return '—';
  const list = Array.isArray(insurers) ? insurers : [];
  const ref = meta.insurer;
  const id = ref && typeof ref === 'object' ? ref._id || ref.id : ref;
  const code = meta.insurerCode || (ref && typeof ref === 'object' ? ref.insurerId : null) || null;

  let doc = ref && typeof ref === 'object' && ref.title ? ref : null;
  if (!doc && list.length) {
    doc =
      (id && list.find(i => String(i._id) === String(id))) || (code && list.find(i => i.insurerId === code)) || null;
  }

  if (doc?.title) {
    const title = (locale && doc.title[locale]) || doc.title['zh-Hant-HK'] || doc.title.zh || doc.title.en;
    if (title) return title;
  }

  const slug = (doc && doc.insurerId) || code;
  if (slug && intl) return tryTranslate(intl, slug);
  if (slug) return slug;
  if (typeof ref === 'string' && ref && !/^[a-fA-F0-9]{24}$/.test(ref)) {
    return intl ? tryTranslate(intl, ref) : ref;
  }
  return '—';
};

export const getProfileParts = (insured, { intl } = {}) => {
  const person = insured || {};
  const gender = person.gender ? (intl ? tryTranslate(intl, person.gender) : person.gender) : null;
  const age =
    person.issueAge != null
      ? intl
        ? `${tryTranslate(intl, 'Age')} ${person.issueAge}`
        : `age ${person.issueAge}`
      : null;
  const smoking = person.smokingStatus
    ? intl
      ? tryTranslate(intl, person.smokingStatus)
      : person.smokingStatus
    : null;
  return [gender, age, smoking].filter(Boolean);
};

export const getProfileLabel = (insured, options) => getProfileParts(insured, options).join(' · ');

export const getRowYearLabel = row =>
  row?.rowLabelVerbatim ||
  (row?.policyYear != null ? String(row.policyYear) : null) ||
  (row?.attainedAge != null ? String(row.attainedAge) : '—');
